import { ThemeProvider, useTheme } from '@mui/material/styles';
import { PropsWithChildren, useMemo } from 'react';

import {
  DISPLAY_HEADING_FONT_WEIGHT,
  FONT_WEIGHTS,
  TITLE_SERIF_SIZING,
} from '@/styles/tokens/fonts';

const PRESENTATION_TEXT_PRIMARY_FONT_SIZE = '1rem'; // 16px
const PRESENTATION_TEXT_PRIMARY_LINE_HEIGHT = '1.375rem'; // 22px

/**
 * Applies presentation-specific typography sizing on top of the active theme.
 * The header font family (`h1_display`/`h2_display`) is inherited from the
 * customer/brand theme — see `makeThemeFromBrand`, which resolves it from the
 * brand's `presentationHeaderFontFace` — so this provider only owns sizing.
 */
export function PresentationTypographyProvider({
  children,
}: PropsWithChildren) {
  const theme = useTheme();

  const presentationTheme = useMemo(() => {
    return {
      ...theme,
      typography: {
        ...theme.typography,
        'body-s': {
          ...theme.typography['body-s'],
          fontSize: PRESENTATION_TEXT_PRIMARY_FONT_SIZE,
          lineHeight: PRESENTATION_TEXT_PRIMARY_LINE_HEIGHT,
          fontWeight: FONT_WEIGHTS.regular,
        },
        h1_display: {
          ...theme.typography.h1_display,
          ...TITLE_SERIF_SIZING.xxl,
          fontWeight: DISPLAY_HEADING_FONT_WEIGHT,
        },
        h2_display: {
          ...theme.typography.h2_display,
          ...TITLE_SERIF_SIZING.l,
          fontWeight: DISPLAY_HEADING_FONT_WEIGHT,
        },
      },
    };
  }, [theme]);

  return <ThemeProvider theme={presentationTheme}>{children}</ThemeProvider>;
}
